<template>
  <div class="recommendation-admin">
    <div class="page-header">
      <div>
        <h1>精准推荐与归因</h1>
        <p>配置确定性题库/章节路由；预评价仅允许明确的预评价报告意图。</p>
      </div>
      <button class="btn btn-primary" type="button" @click="startCreate">新建规则</button>
    </div>

    <div v-if="message" :class="['alert', messageType === 'error' ? 'alert-danger' : 'alert-success']" role="status">{{ message }}</div>

    <form v-if="editing" class="rule-form" @submit.prevent="saveRule">
      <h2>{{ editingId ? '编辑规则' : '新建规则' }}</h2>
      <div class="form-grid">
        <label>题库
          <select v-model.number="form.questionBankId" required @change="loadChapters">
            <option :value="0" disabled>请选择题库</option>
            <option v-for="bank in questionBanks" :key="bank.id" :value="bank.id">{{ bank.description || bank.subdomain }}</option>
          </select>
        </label>
        <label>精确章节（可选）
          <input v-model.trim="form.chapter" list="chapter-list" placeholder="空表示题库级" />
          <datalist id="chapter-list"><option v-for="chapter in chapters" :key="chapter" :value="chapter" /></datalist>
        </label>
        <label>意图
          <select v-model="form.intentCode" required @change="applyDefaultTarget">
            <option v-for="intent in contracts?.intentCodes || []" :key="intent" :value="intent">{{ intent }}</option>
          </select>
        </label>
        <label>用户场景（可选）
          <select v-model="form.audienceRole" @change="applyDefaultTarget">
            <option value="">不限</option>
            <option v-for="role in contracts?.audienceRoles || []" :key="role" :value="role">{{ role }}</option>
          </select>
        </label>
        <label>主目标
          <select v-model="form.primaryTarget" required>
            <option v-for="target in contracts?.targetOptions || []" :key="target" :value="target">{{ target }}</option>
          </select>
        </label>
        <label>次目标（可选）
          <select v-model="form.secondaryTarget">
            <option value="">无</option>
            <option v-for="target in contracts?.targetOptions || []" :key="target" :value="target">{{ target }}</option>
          </select>
        </label>
        <label>优先级
          <input v-model.number="form.priority" type="number" min="-100000" max="100000" required />
        </label>
        <label>审核文案键
          <select v-model="form.copyKey" required>
            <option v-for="copyKey in contracts?.copyKeys || []" :key="copyKey" :value="copyKey">{{ copyKey }}</option>
          </select>
        </label>
      </div>
      <fieldset>
        <legend>允许展示位置</legend>
        <label v-for="surface in contracts?.sourceSurfaces || []" :key="surface" class="check-option">
          <input v-model="form.allowedSurfaces" type="checkbox" :value="surface" /> {{ surface }}
        </label>
      </fieldset>
      <label class="check-option"><input v-model="form.isEnabled" type="checkbox" /> 启用</label>
      <div class="form-actions">
        <button class="btn btn-primary" type="submit">保存</button>
        <button class="btn btn-outline-secondary" type="button" @click="editing = false">取消</button>
      </div>
    </form>

    <section class="panel">
      <h2>推荐规则</h2>
      <div class="table-responsive">
        <table class="table align-middle">
          <thead><tr><th>题库/章节</th><th>意图/场景</th><th>主次目标</th><th>位置</th><th>优先级</th><th>状态/告警</th><th>操作</th></tr></thead>
          <tbody>
            <tr v-for="rule in rules" :key="rule.id">
              <td>{{ rule.questionBank?.description || rule.questionBank?.subdomain || rule.questionBankId }}<small>{{ rule.chapter || '题库级' }}</small></td>
              <td>{{ rule.intentCode }}<small>{{ rule.audienceRole || '不限' }}</small></td>
              <td>{{ rule.primaryTarget }}<small>{{ rule.secondaryTarget || '无次目标' }}</small></td>
              <td>{{ rule.allowedSurfaces.join('、') }}</td>
              <td><strong>{{ rule.priority }}</strong><button type="button" aria-label="提高优先级" @click="changePriority(rule, 1)">↑</button><button type="button" aria-label="降低优先级" @click="changePriority(rule, -1)">↓</button></td>
              <td>
                <span :class="rule.isEnabled ? 'text-success' : 'text-muted'">{{ rule.isEnabled ? '启用' : '停用' }}</span>
                <small v-for="warning in rule.warnings" :key="warning" class="warning">{{ warning === 'chapterMissing' ? '章节已失效' : '题库已禁用' }}</small>
              </td>
              <td class="actions"><button type="button" @click="editRule(rule)">编辑</button><button type="button" @click="toggleRule(rule)">{{ rule.isEnabled ? '停用' : '启用' }}</button><button type="button" class="danger" @click="removeRule(rule)">删除</button></td>
            </tr>
            <tr v-if="!rules.length"><td colspan="7" class="text-center text-muted">暂无规则；所有题库将安全回退到综合学习入口。</td></tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="panel">
      <div class="analytics-header"><h2>漏斗与学习保护指标</h2><button type="button" class="btn btn-outline-primary" @click="loadAnalytics">刷新</button></div>
      <div class="filters">
        <label>开始日期<input v-model="filters.from" type="date" /></label>
        <label>结束日期<input v-model="filters.to" type="date" /></label>
        <label>题库<select v-model="filters.questionBankId"><option value="">全部</option><option v-for="bank in questionBanks" :key="bank.id" :value="bank.id">{{ bank.description || bank.subdomain }}</option></select></label>
        <label>意图<select v-model="filters.intentCode"><option value="">全部</option><option v-for="intent in contracts?.intentCodes || []" :key="intent" :value="intent">{{ intent }}</option></select></label>
        <label>产品/工具<input v-model.trim="filters.productCode" placeholder="productCode 或 tool:id" /></label>
        <label>位置<select v-model="filters.sourceSurface"><option value="">全部</option><option v-for="surface in contracts?.sourceSurfaces || []" :key="surface" :value="surface">{{ surface }}</option></select></label>
      </div>
      <div v-if="analytics" class="metric-grid">
        <article><strong>{{ analytics.funnel.completedSessions || 0 }}</strong><span>完成会话</span></article>
        <article><strong>{{ analytics.funnel.recommendationImpression || 0 }}</strong><span>推荐曝光</span></article>
        <article><strong>{{ analytics.funnel.recommendationClick || 0 }}</strong><span>推荐点击</span></article>
        <article><strong>{{ analytics.funnel.landingView || 0 }}</strong><span>官网落地</span></article>
        <article><strong>{{ analytics.funnel.toolOpen || 0 }}</strong><span>工具打开</span></article>
        <article><strong>{{ analytics.funnel.demoSubmit || 0 }}</strong><span>咨询提交（非有效线索）</span></article>
      </div>
      <div v-if="analytics" class="protection">
        <span>刷题完成率 {{ percent(analytics.protectionMetrics.completionRate) }}</span>
        <span>错题复习率 {{ percent(analytics.protectionMetrics.wrongQuestionReviewRate) }}</span>
        <span>重新开始率 {{ percent(analytics.protectionMetrics.restartRate) }}</span>
        <span>推荐关闭率 {{ percent(analytics.protectionMetrics.recommendationCloseRate) }}</span>
      </div>
      <div v-if="analytics" class="north-star"><strong>{{ analytics.northStar.metric }}：</strong>【待验证】<br /><small>{{ analytics.northStar.verificationStatus }}；当前仅展示每千次完成会话的咨询提交 {{ analytics.northStar.demoSubmitsPer1000CompletedSessions.toFixed(2) }}。</small></div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { recommendationsApi } from '@/api/recommendations'
import { questionBanksApi } from '@/api/questionBanks'
import type { QuestionBank } from '@/types'
import type { IntentCode, RecommendationAnalytics, RecommendationContracts, RecommendationRule, RecommendationRuleInput, SourceSurface } from '@/types/recommendation'

const contracts = ref<RecommendationContracts | null>(null)
const questionBanks = ref<QuestionBank[]>([])
const chapters = ref<string[]>([])
const rules = ref<RecommendationRule[]>([])
const analytics = ref<RecommendationAnalytics | null>(null)
const editing = ref(false)
const editingId = ref<number | null>(null)
const message = ref('')
const messageType = ref<'success' | 'error'>('success')

const emptyForm = (): RecommendationRuleInput => ({
  questionBankId: 0,
  chapter: '',
  intentCode: 'generalLearning',
  audienceRole: undefined,
  primaryTarget: 'zwyHome',
  secondaryTarget: 'occupationalTools',
  priority: 0,
  copyKey: 'generalLearning',
  allowedSurfaces: ['quizStart', 'quizResult', 'dashboard'],
  isEnabled: true,
})
const form = reactive<RecommendationRuleInput>(emptyForm())
const filters = reactive({ from: '', to: '', questionBankId: '' as number | '', intentCode: '' as IntentCode | '', productCode: '', sourceSurface: '' as SourceSurface | '' })

const notify = (text: string, type: 'success' | 'error' = 'success') => { message.value = text; messageType.value = type }
const percent = (value: number) => `${(value * 100).toFixed(1)}%`
const payload = (rule: RecommendationRule): RecommendationRuleInput => ({
  questionBankId: rule.questionBankId,
  chapter: rule.chapter || undefined,
  intentCode: rule.intentCode,
  audienceRole: rule.audienceRole || undefined,
  primaryTarget: rule.primaryTarget,
  secondaryTarget: rule.secondaryTarget || undefined,
  priority: rule.priority,
  copyKey: rule.copyKey,
  allowedSurfaces: [...rule.allowedSurfaces],
  isEnabled: rule.isEnabled,
})

const loadRules = async () => { rules.value = await recommendationsApi.listRules() }
const loadAnalytics = async () => {
  analytics.value = await recommendationsApi.getAnalytics({
    from: filters.from || undefined,
    to: filters.to || undefined,
    questionBankId: filters.questionBankId || undefined,
    intentCode: filters.intentCode || undefined,
    productCode: filters.productCode || undefined,
    sourceSurface: filters.sourceSurface || undefined,
  })
}
const loadChapters = async () => { chapters.value = form.questionBankId ? await questionBanksApi.getChapters(form.questionBankId) : [] }
const applyDefaultTarget = () => {
  const mapping = contracts.value?.defaultMappings[`${form.intentCode}:${form.audienceRole || '*'}`]
    || contracts.value?.defaultMappings[`${form.intentCode}:*`]
  if (mapping) { form.primaryTarget = mapping.primary; form.secondaryTarget = mapping.secondary }
}
const startCreate = () => { Object.assign(form, emptyForm()); editingId.value = null; editing.value = true; chapters.value = [] }
const editRule = async (rule: RecommendationRule) => { Object.assign(form, payload(rule)); editingId.value = rule.id; editing.value = true; await loadChapters() }
const saveRule = async () => {
  try {
    if (!form.allowedSurfaces.length) throw new Error('至少选择一个展示位置')
    if (editingId.value) await recommendationsApi.updateRule(editingId.value, { ...form })
    else await recommendationsApi.createRule({ ...form })
    editing.value = false
    await loadRules()
    notify('规则已保存')
  } catch (error: any) { notify(error.response?.data?.message || error.message || '规则保存失败', 'error') }
}
const toggleRule = async (rule: RecommendationRule) => { await recommendationsApi.updateRule(rule.id, { ...payload(rule), isEnabled: !rule.isEnabled }); await loadRules() }
const changePriority = async (rule: RecommendationRule, delta: number) => {
  try { await recommendationsApi.updateRule(rule.id, { ...payload(rule), priority: rule.priority + delta }); await loadRules() }
  catch (error: any) { notify(error.response?.data?.message || '优先级冲突', 'error') }
}
const removeRule = async (rule: RecommendationRule) => { if (window.confirm('确定删除该推荐规则吗？')) { await recommendationsApi.deleteRule(rule.id); await loadRules() } }

onMounted(async () => {
  const [contractData, bankResponse] = await Promise.all([
    recommendationsApi.getContracts(),
    questionBanksApi.getQuestionBanks({ page: 1, limit: 1000 }),
  ])
  contracts.value = contractData
  questionBanks.value = bankResponse.data
  await Promise.all([loadRules(), loadAnalytics()])
})
</script>

<style scoped>
.recommendation-admin { color: #26324a; }
.page-header, .analytics-header { display: flex; justify-content: space-between; align-items: start; gap: 1rem; margin-bottom: 1rem; }
.page-header h1, .panel h2, .rule-form h2 { margin: 0 0 .35rem; }
.rule-form, .panel { background: #fff; border-radius: 12px; padding: 1.2rem; margin-bottom: 1.25rem; box-shadow: 0 2px 10px rgba(31, 42, 68, .08); }
.form-grid, .filters { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: .9rem; }
label { display: flex; flex-direction: column; gap: .35rem; font-weight: 600; }
input, select { min-height: 38px; border: 1px solid #cbd3e1; border-radius: 7px; padding: .4rem .55rem; }
fieldset { margin: 1rem 0; }
.check-option { display: inline-flex; flex-direction: row; align-items: center; margin: .35rem 1rem .35rem 0; font-weight: 400; }
.check-option input { min-height: auto; }
.form-actions, .actions { display: flex; flex-wrap: wrap; gap: .45rem; }
td small { display: block; color: #6b7280; }
.warning { color: #b45309; font-weight: 600; }
td button { border: 0; background: #edf1fb; border-radius: 5px; margin: .1rem; }
td button.danger { color: #b42318; }
.metric-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: .75rem; margin: 1rem 0; }
.metric-grid article { padding: .9rem; border: 1px solid #e2e6ef; border-radius: 9px; }
.metric-grid strong { display: block; font-size: 1.4rem; }
.protection { display: flex; flex-wrap: wrap; gap: 1rem; padding: .8rem; background: #f5f7fb; }
.north-star { margin-top: .8rem; padding: .8rem; border-left: 4px solid #d97706; background: #fff9ed; }
@media (max-width: 640px) { .page-header { flex-direction: column; } }
</style>
